import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Modal from "../common/Modal";
import Input from "../common/Input";
import Button from "../common/Button";
import {
  adGroupCreateSchema,
  adGroupUpdateSchema,
  AdGroupFormData,
  AdGroupUpdateFormData,
} from "../../schemas/adGroupSchema";

interface AdGroupFormProps {
  isOpen: boolean;
  initialData?: AdGroupFormData | AdGroupUpdateFormData;
  onSubmit: (data: AdGroupFormData | AdGroupUpdateFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
  isUpdate?: boolean;
}

const AdGroupForm: React.FC<AdGroupFormProps> = ({
  isOpen,
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
  isUpdate = false,
}) => {
  const {
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    trigger,
  } = useForm<AdGroupFormData | AdGroupUpdateFormData>({
    resolver: zodResolver(isUpdate ? adGroupUpdateSchema : adGroupCreateSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      name: initialData?.name || "",
      description: initialData?.description || "",
    },
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title={isUpdate ? "Edit Ad Group" : "Create Ad Group"}
      size="md"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" noValidate>
        <Input
          label="Name"
          placeholder="Enter name"
          name="name"
          value={watch("name") || ""}
          onChange={(e) => setValue("name", e.target.value, { shouldValidate: false })}
          onBlur={() => trigger("name")}
          error={errors.name}
        />

        <Input
          label="Description"
          placeholder="Optional"
          name="description"
          value={watch("description") || ""}
          onChange={(e) => setValue("description", e.target.value, { shouldValidate: false })}
          onBlur={() => trigger("description")}
          error={errors.description}
        />

        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button type="submit" variant="primary" isLoading={isLoading}>
            {isUpdate ? "Update" : "Create"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AdGroupForm;
